import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import TcnApp from '../../../components/tcn/TcnApp';
import TopBar from '../../../components/tcn/layout/TopBar';
import { Card, Avatar, Icon, Chip, CTAButton } from '../../../components/tcn/ui-kit';
import { TCN } from '../../../components/tcn/ui-kit/tokens';
import CreatorMetricView from '../../../components/tcn-modules/m4/CreatorMetricView';
import ActionPlanModal from '../../../components/tcn-modules/m4/ActionPlanModal';
import SectionHeader from '../../../components/tcn-modules/shared/SectionHeader';
import TierChip from '../../../components/tcn-modules/shared/TierChip';
import TapCopy from '../../../components/TapCopy';

/**
 * /tcn/me/creators/:id — Module 4 Creator detail.
 *
 * SELECT from creator_current_month WHERE creator_id = :id (RLS verifies the
 * creator belongs to the current manager).
 *
 * Single scrollable screen: the 4 metric blocks (Diamonds/PK/Rank-Up/Activeness,
 * A4.2–A4.5) are stacked as mockup-faithful rich blocks (CreatorMetricView:
 * BigStat hero + pace bar, PK big numbers + efficiency sparkline, tier→forecast,
 * activeness level badge), each under a big-title SectionHeader — no internal
 * tabs (transverse principle). A4.7 action plan = modal.
 */
const TcnCreatorDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  const [creator, setCreator] = useState(null);
  const [decrypted, setDecrypted] = useState(null);
  const [decrypting, setDecrypting] = useState(false);
  const [decryptError, setDecryptError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [latestActionPlan, setLatestActionPlan] = useState(null);
  const [usdRate, setUsdRate] = useState(0.01);

  const load = useCallback(async () => {
    if (!id || !user?.id) return;
    setLoading(true);
    setError(null);
    setDecrypted(null);
    setDecryptError(null);
    const { data, error } = await supabase
      .from('creator_current_month')
      .select('*')
      .eq('creator_id', id)
      .maybeSingle();
    if (error) {
      setError(error);
    } else {
      setCreator(data);
      // Fetch the latest action plan if linked
      if (data?.latest_action_plan_id) {
        const { data: ap } = await supabase
          .from('action_log')
          .select('id, title, description, status, due_date, created_at')
          .eq('id', data.latest_action_plan_id)
          .maybeSingle();
        setLatestActionPlan(ap || null);
      } else {
        setLatestActionPlan(null);
      }
    }
    // USD rate (server-scoped MENA for TCN — R7)
    const { data: rateRow } = await supabase
      .from('metric_thresholds')
      .select('target_value')
      .eq('metric_name', 'diamond_to_usd_rate')
      .eq('server', 'MENA')
      .maybeSingle();
    if (rateRow?.target_value != null) {
      const n = Number(rateRow.target_value);
      if (!Number.isNaN(n) && n > 0) setUsdRate(n);
    }
    setLoading(false);

    // Phase A: decrypt creator-level financials via Edge Function.
    // (creator_current_month keeps `*_enc` only — super_admin can SELECT it,
    //  so we cannot mirror clear columns there. R2-safe by design.)
    if (data?.creator_id) {
      setDecrypting(true);
      try {
        const { data: decResp, error: decErr } = await supabase.functions.invoke(
          'tcn-decrypt-creator',
          { body: { creatorId: data.creator_id } },
        );
        if (decErr) {
          setDecryptError(decErr);
        } else {
          // Edge Function returns { <creatorId>: { diamonds_mtd, ... } }
          setDecrypted(decResp?.[data.creator_id] ?? null);
        }
      } catch (e) {
        setDecryptError(e);
      } finally {
        setDecrypting(false);
      }
    }
  }, [id, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleTabChange = (tab) => {
    if (tab === 'today') return navigate('/tcn/me');
    if (tab === 'creators') return navigate('/tcn/me/creators');
    return navigate(`/tcn/me?todo=${tab}`);
  };

  const toggle1on1 = async (next) => {
    if (!creator) return;
    setCreator((c) => ({ ...c, is_in_today_1on1_list: next }));
    // Audit fix #41 — call the SECURITY DEFINER RPC instead of writing directly
    // to creator_current_month. Frontends can no longer overwrite arbitrary
    // columns (e.g. encrypted financial fields).
    const { error } = await supabase.rpc('set_creator_1on1', {
      p_creator_id: creator.creator_id,
      p_value: next,
    });
    if (error) {
      // Rollback optimistic change
      setCreator((c) => ({ ...c, is_in_today_1on1_list: !next }));
      console.error('[1on1 toggle] error', error);
    } else if (next) {
      // Best-effort insert into history (RLS will reject if not owner — silent)
      try {
        await supabase.from('daily_1on1_history').insert({
          tenant_id: creator.tenant_id,
          agency_id: creator.agency_id,
          agent_id: user.id,
          creator_id: creator.creator_id,
        });
      } catch {
        /* ignore — table may not exist or RLS may block */
      }
      // Audit fix #48 — also create an action_log entry so the 1-on-1 surfaces
      // in Module 6 (cross-module workflow). Best-effort, fire-and-forget.
      const today = new Date().toISOString().slice(0, 10);
      const username = creator.tiktok_username || creator.display_name || 'creator';
      try {
        await supabase.from('action_log').insert({
          tenant_id: creator.tenant_id,
          agency_id: creator.agency_id,
          agent_id: user.id,
          source_activity: 'one_on_one_call',
          source_entity_type: 'creator',
          source_entity_id: creator.creator_id,
          title: `1-on-1 with @${username}`,
          description: 'Scheduled from creator detail page',
          due_date: today,
          status: 'open',
        });
      } catch {
        /* ignore */
      }
    }
  };

  const handleSubmitActionPlan = async (payload) => {
    if (!creator || !user?.id) return;
    const { data, error } = await supabase
      .from('action_log')
      .insert({
        tenant_id: creator.tenant_id,
        agency_id: creator.agency_id,
        agent_id: user.id,
        ...payload,
      })
      .select('id, title, description, status, due_date, created_at')
      .single();
    if (error) throw error;
    // Update the creator row's latest_action_plan_id pointer (best-effort)
    await supabase
      .from('creator_current_month')
      .update({
        latest_action_plan_id: data.id,
        has_open_action_plan: true,
      })
      .eq('creator_id', creator.creator_id);
    setLatestActionPlan(data);
    setCreator((c) => ({
      ...c,
      latest_action_plan_id: data.id,
      has_open_action_plan: true,
    }));
  };

  const userName = creator?.display_name || creator?.tiktok_username || '...';

  return (
    <TcnApp
      tab="creators"
      onTabChange={handleTabChange}
      desktopBreadcrumb={[
        t('tcn.tabs.creators', 'Creators'),
        userName ? `@${creator?.tiktok_username || userName}` : '',
      ].filter(Boolean)}
      mobileHeader={
        <TopBar
          leading={
            <button
              type="button"
              onClick={() => navigate('/tcn/me/creators')}
              style={{
                background: 'transparent',
                border: 'none',
                color: TCN.cyan,
                cursor: 'pointer',
                padding: 4,
              }}
            >
              <Icon name="chevron-left" size={20} />
            </button>
          }
          title={creator ? `@${creator.tiktok_username || creator.display_name || ''}` : '...'}
          subtitle={creator?.group_label || ''}
        />
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          padding: 14,
          maxWidth: 760,
          margin: '0 auto',
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: TCN.textDim }}>
            {t('tcn.m4.detail.loading', 'Loading creator…')}
          </div>
        ) : error ? (
          <Card accent="red">
            <div style={{ textAlign: 'center', padding: 16, color: TCN.red }}>
              {error.message || t('tcn.m4.detail.error', 'Could not load creator')}
            </div>
          </Card>
        ) : !creator ? (
          <Card>
            <div style={{ textAlign: 'center', padding: 18, color: TCN.textDim }}>
              {t('tcn.m4.detail.notFound', 'Creator not found.')}
            </div>
          </Card>
        ) : (
          <>
            {/* Identity card */}
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <Avatar name={creator.display_name || creator.tiktok_username || ''} size={56} ring />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: TCN.text,
                      letterSpacing: '-0.02em',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <TapCopy
                      value={creator.tiktok_username || creator.display_name || creator.creator_id}
                      title={t('tcn.creator.copyHandle', 'Tap to copy handle')}
                    >
                      @{creator.tiktok_username || creator.display_name || creator.creator_id}
                    </TapCopy>
                  </div>
                  {creator.display_name && creator.tiktok_username && (
                    <div style={{ fontSize: 13, color: TCN.textDim }}>{creator.display_name}</div>
                  )}
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    <TierChip tier={creator.tier_current} />
                    {creator.server && <Chip color="cyan" size="sm">{creator.server}</Chip>}
                    {creator.group_label && (
                      <Chip color="cyan" size="sm">
                        {creator.group_label}
                      </Chip>
                    )}
                    {creator.days_since_joining != null && (
                      <Chip color="diamond" size="sm">
                        D{creator.days_since_joining}
                      </Chip>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* A4.2–A4.5 — 4 rich metric blocks stacked (single scrollable screen,
                no internal tabs — transverse principle). Each block is the
                mockup-faithful CreatorMetricView (BigStat hero + pace bar for
                Diamonds, big numbers + efficiency sparkline for PK, tier→forecast
                for Rank-Up, level badge for Activeness), coiffed by a big-title
                SectionHeader. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <SectionHeader
                title={t('tcn.m4.detail.diamondsTitle', 'Diamonds')}
                subtitle={t('tcn.m4.view.diamond.subtitle', 'MTD detail')}
              />
              <CreatorMetricView
                view="diamond"
                creator={creator}
                decrypted={decrypted}
                decrypting={decrypting}
                usdRate={usdRate}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <SectionHeader
                title={t('tcn.m4.detail.pkTitle', 'PK / Matches')}
                subtitle={t('tcn.m4.view.pk.subtitle', 'Performance detail')}
              />
              <CreatorMetricView
                view="pk"
                creator={creator}
                decrypted={decrypted}
                decrypting={decrypting}
                effSeries={creator.pk_efficiency_series || []}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <SectionHeader title={t('tcn.m4.detail.rankUpTitle', 'Rank-Up / Maintain')} />
              <CreatorMetricView
                view="rankup"
                creator={creator}
                decrypted={decrypted}
                decrypting={decrypting}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <SectionHeader title={t('tcn.m4.detail.activenessTitle', 'Activeness')} />
              <CreatorMetricView
                view="active"
                creator={creator}
                decrypted={decrypted}
                decrypting={decrypting}
              />
            </div>

            {decryptError && (
              <div style={{ fontSize: 11, color: TCN.amber, fontStyle: 'italic', paddingInline: 4 }}>
                {t(
                  'tcn.m4.detail.decryptError',
                  'Could not decrypt some values. Refresh to retry.'
                )}
              </div>
            )}

            {/* Action toggles — 1-on-1 + create action plan (A4.6/A4.7 entry) */}
            <Card>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => toggle1on1(!creator.is_in_today_1on1_list)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 0',
                    background: 'transparent',
                    border: 'none',
                    color: TCN.text,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: TCN.fontSans,
                  }}
                >
                  <span>{t('tcn.m4.detail.add1on1', "Add to today's 1-on-1")}</span>
                  <Chip
                    size="sm"
                    color={creator.is_in_today_1on1_list ? 'green' : 'cyan'}
                    solid={!!creator.is_in_today_1on1_list}
                  >
                    {creator.is_in_today_1on1_list
                      ? t('tcn.m4.detail.added', 'Added')
                      : t('tcn.m4.detail.add', 'Add')}
                  </Chip>
                </button>
                {(creator.alert_status || 'none') !== 'none' && (
                  <CTAButton
                    variant="primary"
                    size="md"
                    onClick={() => setActionModalOpen(true)}
                    leading={<Icon name="plus" size={16} />}
                  >
                    {t('tcn.m4.detail.createActionPlan', 'Create action plan')}
                  </CTAButton>
                )}
              </div>
            </Card>

            {/* Latest action plan */}
            {latestActionPlan && (
              <Card>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: TCN.textDim,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    marginBottom: 8,
                  }}
                >
                  {t('tcn.m4.detail.latestPlan', 'Latest action plan')}
                </div>
                <div
                  style={{ fontSize: 15, fontWeight: 700, color: TCN.text, marginBottom: 4 }}
                >
                  {latestActionPlan.title}
                </div>
                <div style={{ fontSize: 13, color: TCN.textDim, lineHeight: 1.4 }}>
                  {latestActionPlan.description}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: TCN.textMute,
                    marginTop: 8,
                    fontFamily: TCN.fontMono,
                  }}
                >
                  {t('tcn.m4.detail.due', 'Due: {{date}}', { date: latestActionPlan.due_date })}{' '}
                  · {latestActionPlan.status}
                </div>
              </Card>
            )}
          </>
        )}
      </div>

      <ActionPlanModal
        open={actionModalOpen}
        onClose={() => setActionModalOpen(false)}
        onSubmit={handleSubmitActionPlan}
        creator={creator}
      />
    </TcnApp>
  );
};

export default TcnCreatorDetail;
